/**
 * Roles authorized to approve or decline an opportunity.
 */
export enum OpportunityApproverRoles {
  operationsVP = "operationsVP",
  ceo = "ceo"
}

/**
 * An acquisition opportunity record, including deal terms, location, contacts, and per-approver (land VP, operations VP, CEO) decision/status fields.
 */
export interface Opportunity {
  aimOpportunity: number;
  title: string;
  status: string;
  priority: string;
  description: string;
  // aimDivision: string;
  aimAssetType: string;
  aimManagementType: string;
  aimSaleType: string;
  aimTransactionType: string;
  estPrice: number;
  estAcres: number;
  bidDate: string | undefined;
  estCompletion: string | undefined;
  highValueDeal: boolean;
  landVPScreens: boolean;
  landVP: number | undefined;
  landVPName: string | undefined;
  landVPActor: string | undefined;
  landVPDecision: string | undefined;
  landVPReason: string | undefined;
  landVPComment: string | undefined;
  landVPDecidedAt: string | undefined;
  operationsVP: number | undefined;
  operationsVPName: string | undefined;
  operationsVPActor: string | undefined;
  operationsVPDecision: string | undefined;
  operationsVPReason: string | undefined;
  operationsVPComment: string | undefined;
  operationsVPDecidedAt: string | undefined;
  ceo: number | undefined;
  ceoName: string | undefined;
  ceoActor: string | undefined;
  ceoDecision: string | undefined;
  ceoReason: string | undefined;
  ceoComment: string | undefined;
  ceoDecidedAt: string | undefined;
  createdOn: string;
  createdBy: string;
  canceledBy: string | undefined;
  canceledOn: string | undefined;
  canceledReason: string | undefined;
  pendingLand: boolean;
  pendingOperations: boolean;
  pendingCEO: boolean;
  canCancel: boolean;
  canSubmit: boolean;
  canUpdate: boolean;
  country: string;
  stateProvince: string;
  county: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  internalContactName: string;
}

/**
 * A blank/default Opportunity object with all fields set to their initial empty or false values, used as a template for creating new opportunities.
 */
export const blankOpportunity: Opportunity = {
  aimOpportunity: 0,
  title: '',
  status: 'Draft',
  priority: '',
  description: '',
  // aimDivision: '',
  aimAssetType: '',
  aimManagementType: '',
  aimSaleType: '',
  aimTransactionType: '',
  //@ts-ignore
  estPrice: undefined,
  //@ts-ignore
  estAcres: undefined,
  bidDate: '',
  estCompletion: '',
  highValueDeal: false,
  landVPScreens: false,
  landVP: 0,
  landVPName: '',
  landVPActor: '',
  landVPDecision: '',
  landVPReason: '',
  landVPComment: '',
  landVPDecidedAt: '',
  operationsVP: 3,
  operationsVPName: '',
  operationsVPActor: '',
  operationsVPDecision: '',
  operationsVPReason: '',
  operationsVPComment: '',
  operationsVPDecidedAt: undefined,
  ceo: 0,
  ceoName: '',
  ceoActor: '',
  ceoDecision: '',
  ceoReason: '',
  ceoComment: '',
  ceoDecidedAt: '',
  createdOn: '',
  createdBy: '',
  canceledBy: undefined,
  canceledOn: undefined,
  canceledReason: undefined,
  pendingLand: false,
  pendingOperations: false,
  pendingCEO: false,
  canCancel: false,
  canSubmit: false,
  canUpdate: false,
  country: '',
  stateProvince: '',
  county: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  internalContactName: '',
}

/**
 * A codified reason usable for opportunity/transaction-team decline decisions, scoped by which decision contexts it's valid for.
 */
export interface ReasonCode {
  aimReasonCode: string;
  description: string;
  validForFailedTeams: boolean;
  validForTeamRejections: boolean;
  validForOpportunities: boolean;
  validForTransactionTeams: boolean;
  validForPositiveDecisions: boolean;
  validForNegativeDecisions: boolean;
  useRequiresComment: boolean;
  active: boolean;
}

/**
 * A mapping from opportunity status strings to their corresponding color hex codes used in UI displays.
 */
export const opportunityStatusColorIndex: Record<string, string> = {
  Draft: '#08979c',
  Mapping: '#722ed1',
  Pending: '#d4b106',
  Qualified: '#389e0d',
  Canceled: 'rgba(245,34,45,0.78)',
  Declined: 'rgba(245,34,45,0.78)'
};

/**
 * An array of all possible opportunity status string values.
 */
export const opportunityStatuses = ["Draft", "Mapping", "Pending", "Qualified", "Canceled", "Declined"];

/**
 * Returns true if the opportunity's status is 'Canceled' or 'Declined'.
 */
export const isCanceled = (opportunity: Opportunity) => opportunity.status === "Canceled" || opportunity.status === "Declined";
/**
 * Returns true if the opportunity's status is 'Qualified'.
 */
export const isQualified = (opportunity: Opportunity) => opportunity.status === "Qualified";

/**
 * A mapping from opportunity priority strings (Low, Medium, High) to their corresponding color hex codes.
 */
export const opportunityPriorityColorIndex = {
  Low: '#389e0d',
  Medium: '#d4b106',
  High: 'rgba(245,34,45,0.78)'
};

/**
 * Enum of executive decision action strings used for opportunity qualification and decline workflows.
 */
export enum executiveDecision {
  'qualifyOperations',
  'declineOperations',
  'qualifyCeo',
  'declineCeo'
}

/**
 * Enum of possible save actions for an opportunity form (SaveNew, SubmitDecisions, UpdateExisting, NoAction).
 */
export enum opportunityFormSaveActions {
  'SaveNew',
  'SubmitDecisions',
  'UpdateExisting',
  'NoAction'
}
